<template>
  <div
    class="d-flex-column justify-start align-start work_with_us_spacing gap-32"
    id="open-positions"
  >
    <div class="d-flex-column justify-start align-start gap-10">
      <p
        class="regular-s font-family-2 t-orange caps"
        style="letter-spacing: 2.24px"
      >
        {{ $t("work_with_us_2.requirements") }}
      </p>
      <h1 class="t-white font-family-1 title-h2 f-w-600">
        {{ $t("work_with_us_2.open_positions") }}
      </h1>
    </div>

    <div class="d-flex-row justify-start game_tabs hide_scroll" v-dragscroll.x>
      <button
        class="dark tab _plain b-radius-6 small justify-start full-width max-width-213px height-48 white-space-no gap-12 font-family-2 medium-l"
        style="min-width: 200px"
        :class="[active_tab === null && 'active_tab']"
        @click="selectTab(null)"
      >
        <HoodedBoosterPlain class="game_icon" />
        <label class="t-white bold-xl font-family-2 cursor-pointer">{{
          $t("work_with_us_2.all_roles")
        }}</label>
      </button>
      <button
        class="dark tab _plain b-radius-6 small game_picker_button justify-start full-width max-width-213px height-48 white-space-no gap-12 font-family-2 medium-l"
        style="min-width: 200px"
        v-for="game in sortedGames"
        :key="game.slug"
        :class="[active_tab === game.slug && 'active_tab']"
        @click="selectTab(game.slug)"
      >
        <img
          class="game_icon"
          :src="game.icon"
          :alt="game.title + ' game image'"
        />
        <label class="t-white bold-l font-family-2 cursor-pointer">{{
          game.title
        }}</label>
        <div
          class="job_count bold-s font-family-2"
          v-if="game_jobs[game.title] > 0"
        >
          {{ game_jobs[game.title] }}
        </div>
      </button>
    </div>

    <div
      class="d-flex-column justify-start align-start gap-16"
      v-if="jobs.length > 0"
    >
      <PositionLink
        v-for="job in jobs"
        :key="job._id"
        :title="job.title"
        :game="job.game"
        :type="job.jobType"
        :servers="job.servers"
        :short-description="job.shortDescription"
        :game-slug="gameSlugMap[job.game]"
        :slug="job.slug.current"
      />
    </div>

    <div
      class="d-flex-column gap-16 no_open_position b-radius-6"
      v-if="jobs.length <= 0 && !loading"
    >
      <p class="t-light regular-l font-family-2 text-center">
        We currently don't have any job openings. Please check back later for
        future opportunities!
      </p>
    </div>
    <div
      class="d-flex-column justify-start align-start gap-16"
      v-if="jobs.length <= 0 && !loading"
    ></div>
    <button
      v-if="hasMore"
      class="primary outlined-color b-radius-9 font-family-1 orange cta_button height-48 xxl regular-l f-w-600 full-width"
      @click="fetchJobs()"
      :class="[loading && 'loading']"
    >
      {{ $t("work_with_us_2.show_more") }}
    </button>
  </div>
</template>

<script setup>
import { sortedGames } from "~/configurations/games.config";
import HoodedBoosterPlain from "../icons/HoodedBoosterPlain.vue";
import PositionLink from "./PositionLink.vue";
import sanityClient from "~/lib/sanityClient";
import {
  gameSlugMap,
  reversedGameSlugMap,
} from "~/configurations/games.config";
const active_tab = ref(null);
const jobs = ref([]);
const page = ref(0);
const limit = 6;
const loading = ref(false);
const hasMore = ref(true);
const game_jobs = ref({});

const fetchJobsPerGame = async () => {
  try {
    const query = `
      *[_type == "job-posting" && status == "published" && defined(game)]{
        game
      }
    `;

    const jobs = await sanityClient.fetch(query);

    const counts = jobs.reduce((acc, job) => {
      acc[job.game] = (acc[job.game] || 0) + 1;
      return acc;
    }, {});
    // const result = Object.entries(counts).map(([game, count]) => ({
    //   game,
    //   count,
    // }));

    game_jobs.value = { ...counts };
    console.log(game_jobs.value);
  } catch (e) {
    console.error("Error fetching job counts per game:", e);
    return [];
  }
};

const fetchJobs = async (reset = false) => {
  if (loading.value || (!hasMore.value && !reset)) return;

  loading.value = true;

  const start = reset ? 0 : page.value * limit;
  const gameSlug = active_tab.value;

  try {
    // Query to get the total count of published job postings
    const countQuery = `
      count(*[_type == "job-posting" && status == "published" ${
        gameSlug ? `&& game == "${reversedGameSlugMap[gameSlug]}"` : ""
      }])
    `;
    const totalCount = await sanityClient.fetch(countQuery);

    // Query to fetch job postings (with pagination)
    const query = `
      *[_type == "job-posting" && status == "published" ${
        gameSlug ? `&& game == "${reversedGameSlugMap[gameSlug]}"` : ""
      }]
      | order(_createdAt desc) [${start}...${start + limit}] {
        _id,
        title,
        slug,
        shortDescription,
        jobType,
        game,
        servers,
        platforms
      }
    `;
    const newJobs = await sanityClient.fetch(query);

    // Update the jobs array and pagination
    if (reset) {
      jobs.value = newJobs;
      page.value = 1;
    } else {
      jobs.value.push(...newJobs);
      page.value += 1;
    }

    // Determine if there are more jobs to fetch
    hasMore.value = start + newJobs.length < totalCount;
  } catch (e) {
    console.error("Error fetching jobs:", e);
  } finally {
    loading.value = false;
  }
};

const selectTab = (tab) => {
  active_tab.value = tab;
  hasMore.value = true;
  jobs.value = [];
  fetchJobs(true); // Reset and refetch
};

// Initial load
fetchJobs(true);
fetchJobsPerGame();
</script>

<style lang="scss">
.game_tabs {
  overflow: auto;
  .game_icon {
    width: 25px;
    height: 25px;
  }
}
.no_open_position {
  width: 100%;
  padding: 24px 12px;
  border: 1px solid var(--stroke-medium);
}

.game_picker_button {
  position: relative;
  .job_count {
    position: absolute;
    top: 5px;
    right: 5px;
    height: 14px;
    width: 14px;
    padding: 3px;
    justify-content: center;
    align-items: center;
    display: flex;
    color: var(--text-black);
    background-color: var(--blue);
    border-radius: 50%;
  }
}
</style>
